import React, { useContext } from "react";
import { motion } from "framer-motion";
import StyleContext from "../../contexts/StyleContext";
import { blogSection } from "../../portfolio";

export default function BlogsModern() {
  const { isDark } = useContext(StyleContext);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className="relative py-20 md:py-32 overflow-hidden" id="blogs">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-rose-50 to-pink-50 dark:from-gray-900 dark:via-rose-900/20 dark:to-pink-900/20" />

      {/* Floating Orb */}
      <motion.div
        className="absolute left-0 top-1/4 w-96 h-96 bg-gradient-to-r from-rose-400 to-pink-400 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 9,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-block mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <span className="px-4 py-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full text-sm font-semibold shadow-lg">
              ✍️ Latest Articles
            </span>
          </motion.div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${
            isDark ? "text-white" : "text-gray-900"
          }`}>
            {blogSection.title}
          </h2>
          <p className={`text-lg md:text-xl max-w-3xl mx-auto ${
            isDark ? "text-gray-400" : "text-gray-600"
          }`}>
            {blogSection.subtitle}
          </p>
        </motion.div>

        {/* Blogs Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
        >
          {blogSection.blogs.map((blog, index) => (
            <motion.a
              key={index}
              href={blog.url}
              target="_blank"
              rel="noopener noreferrer"
              variants={cardVariants}
              whileHover={{
                y: -10,
                transition: { duration: 0.3 }
              }}
              className="glass backdrop-blur-xl rounded-3xl overflow-hidden bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/20 shadow-xl hover:shadow-2xl hover:shadow-rose-500/20 transition-all duration-300 block group"
            >
              {/* Blog Image */}
              <div className="relative h-56 overflow-hidden bg-gradient-to-br from-rose-500/20 to-pink-500/20">
                {blog.image ? (
                  <motion.img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.4 }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-6xl">📝</div>
                  </div>
                )}

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Read More Badge */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="px-3 py-1 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-full text-xs font-semibold text-rose-600 dark:text-rose-400 flex items-center gap-1">
                    <span>Read More</span>
                    <svg className="w-3 h-3" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Date Badge (if available) */}
                {blog.date && (
                  <div className="mb-3">
                    <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                      isDark
                        ? "bg-rose-500/20 text-rose-300"
                        : "bg-rose-100 text-rose-700"
                    }`}>
                      {blog.date}
                    </span>
                  </div>
                )}

                <h3 className={`text-xl font-bold mb-3 leading-tight group-hover:text-rose-500 dark:group-hover:text-rose-400 transition-colors duration-300 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}>
                  {blog.title}
                </h3>

                <p className={`text-sm leading-relaxed line-clamp-3 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}>
                  {blog.description}
                </p>

                {/* Read More Link */}
                <div className="mt-4 flex items-center gap-2 text-rose-500 dark:text-rose-400 font-medium text-sm group-hover:gap-3 transition-all duration-300">
                  <span>Read Article</span>
                  <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>

              {/* Bottom Accent */}
              <div className="h-1 bg-gradient-to-r from-rose-500 to-pink-500" />
            </motion.a>
          ))}
        </motion.div>

        {/* View All Blogs Button */}
        {blogSection.blogs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="flex justify-center mt-12"
          >
            <motion.a
              href="https://benyusouf.medium.com"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-rose-500/50 transition-all duration-300 flex items-center gap-2"
            >
              <span>View All Articles</span>
              <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </motion.a>
          </motion.div>
        )}

        {/* Bottom Decorative Element */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-20 flex justify-center"
        >
          <div className="flex items-center gap-2">
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-rose-500 to-transparent" />
            <div className="w-2 h-2 rounded-full bg-rose-500" />
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-pink-500 to-transparent" />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
